import type { DependencyList, ReactNode } from "react";
import { useEffect, useRef } from "react";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import {
    usePlayerStateController,
    usePlayerStateDirective,
} from "@rosette/web_components/src/game/playerstate/PlayerStateUIContext.js";
import { CanvasBounds, CanvasRenderer, RenderedCanvas } from "@rosette/web_components/src/generic/render/RenderedCanvas.js";
import type { PlayerStateController } from "@rosette/ts/src/business/game/controller/playerstate/PlayerStateController.js";
import type { PlayerStateDirective } from "@rosette/ts/src/business/game/controller/playerstate/PlayerStateDirective.js";
import { drawCircularShadow, drawPiece } from "@/components/game/render/render.js";
import { Vec2 } from "@rosette/ts/src/util/Vec2.js";
import {
    useGameController,
    useGameSource,
    useIsPlayerConnected,
} from "@/components/game/GameUIContext.js";
import { areDependenciesEqual } from "@rosette/ts/src/util/areDependenciesEqual.js";
import type { GameTheme } from "@/components/game/theme/GameTheme.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import { PlayerDescPopupButton } from "@/components/game/playerstate/PlayerDesc.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { defaultGamePreferences } from "@rosette/ts/src/business/api/api_schema.js";
import { ReactionUI } from "@/components/game/reactions/ReactionUI.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import type { SharedRendererState } from "@rosette/web_components/src/game/SharedRendererState.js";
import { Fade, FadeDirection } from "@rosette/ts/src/business/fades.js";
import type { Seconds } from "@rosette/ts/src/util/units.js";
import { max } from "@rosette/ts/src/util/numbers.js";
import type { GameLayoutType } from "@rosette/web_components/src/game/useGameLayout.js";
import { ReplayPlayerStatesDirective } from "@rosette/ts/src/business/game/controller/playerstate/ReplayPlayerStatesDirective.js";
import { assertNever } from "@rosette/ts/src/util/tsutils.js";


enum PlayerStateValue {
    PIECES,
    SCORE,
}


class PlayerStateRenderer extends CanvasRenderer {
    public static readonly PIECE_FADE_DURATION: Seconds = 0.2;

    private readonly playerType: PlayerType;
    private readonly maxPieces: number;
    private readonly alignLeft: boolean;
    private readonly alignTop: boolean;
    private readonly displayValue: PlayerStateValue;
    private readonly theme: GameTheme;

    private readonly controller: PlayerStateController;
    private readonly sharedRendererState: SharedRendererState;

    private readonly fades: Fade[] = [];
    private lastDirective: PlayerStateDirective | null = null;

    constructor(
        playerType: PlayerType,
        alignLeft: boolean,
        alignTop: boolean,
        displayValue: PlayerStateValue,
        maxPieces: number,
        theme: GameTheme,
        controller: PlayerStateController,
        sharedRendererState: SharedRendererState,
    ) {
        super();
        this.playerType = playerType;
        this.maxPieces = maxPieces;
        this.alignLeft = alignLeft;
        this.alignTop = alignTop;
        this.displayValue = displayValue;
        this.theme = theme;

        this.controller = controller;
        this.sharedRendererState = sharedRendererState;
    }

    override attachListeners(): () => void {
        return () => {};
    }

    override calculateRenderBounds(bounds: CanvasBounds): CanvasBounds {
        // There is a bug on some obscure versions of Android when running
        // chrome. The content of canvases of the same size will randomly
        // swap with one another when they are rendered in the same frame.
        // This is the least dumb solution I could find to this...
        const diff = (this.alignLeft ? 0 : 1) + (this.alignTop ? 0 : 2);
        const widthScale = Math.max(1, bounds.width / bounds.height);
        const heightScale = Math.max(1, bounds.height / bounds.width);
        return new CanvasBounds(
            bounds.rect,
            Math.ceil(bounds.width + diff * widthScale),
            Math.ceil(bounds.height + diff * heightScale),
        );
    }

    private getFade(index: number): Fade {
        while (this.fades.length < index + 1) {
            this.fades.push(new Fade(
                PlayerStateRenderer.PIECE_FADE_DURATION,
                PlayerStateRenderer.PIECE_FADE_DURATION,
            ));
        }
        return this.fades[index];
    }

    getMoveIndex(): number {
        return this.sharedRendererState.currentMoveIndex.get();
    }

    getDisplayValue(directive: PlayerStateDirective): number {
        const playerState = directive.getPlayerState(this.playerType, this.getMoveIndex());
        switch (this.displayValue) {
            case PlayerStateValue.PIECES:
                return playerState.getPieceCount();
            case PlayerStateValue.SCORE:
                return playerState.getScore();
            default:
                return assertNever(this.displayValue);
        }
    }

    private onNewDirective(directive: PlayerStateDirective) {
        this.sharedRendererState.highlightIntroducedPiece = null;

        const value = this.getDisplayValue(directive);
        for (let index = 0; index < this.maxPieces; ++index) {
            this.getFade(index).fade(
                index < value ? FadeDirection.IN : FadeDirection.OUT,
            );
        }
    }

    override render(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
    ): void {
        let directive = this.controller.getActiveDirective();
        if (directive instanceof ReplayPlayerStatesDirective) {
            directive = directive.getState(this.getMoveIndex());
        }
        if (directive !== this.lastDirective) {
            this.lastDirective = directive;
            this.onNewDirective(directive);
        }

        this.clearCanvas(ctx, width, height);

        const minSideLength = Math.min(width, height);
        const maxSideLength = Math.max(width, height);
        const pieceWidth = minSideLength / 1.1;
        const stride = (maxSideLength - minSideLength) / max(6, this.maxPieces - 1);
        const xStride = (height < width ? stride : 0);
        const yStride = (width < height ? stride : 0);

        // Get the number of pieces to display.
        let value = this.getDisplayValue(directive);
        if (stride < pieceWidth / 10) {
            value = Math.min(value, 1);
        }

        // Draw a shadow if the last piece is not fully opaque.
        if (this.getFade(this.maxPieces - 1).get() < 1) {
            const xOffset = minSideLength / 2;
            const yOffset = minSideLength / 2;
            const x = (this.alignLeft ? xOffset : width - xOffset);
            const y = (this.alignTop ? yOffset : height - yOffset);
            drawCircularShadow(
                ctx, x, y, pieceWidth / 2.5,
                0, 0, 0, 0.5,
            );
        }

        const centres: Vec2[] = [];
        for (let index = 0; index < this.maxPieces; ++index) {
            const xOffset = minSideLength / 2 + index * xStride;
            const yOffset = minSideLength / 2 + index * yStride;
            const x = (this.alignLeft ? xOffset : width - xOffset);
            const y = (this.alignTop ? yOffset : height - yOffset);
            centres.push(new Vec2(x, y));
        }

        const pieceImg = this.theme.getPieceImage(this.playerType, false);
        const highlightPieceImg = this.theme.getPieceImage(this.playerType, true);
        const highlightOne = (
            this.displayValue === PlayerStateValue.PIECES
            && this.playerType === this.sharedRendererState.highlightIntroducedPiece
        );
        const reverseCentreIndices = (!this.alignTop || width >= height);
        try {
            ctx.save();
            for (let index = 0; index < this.maxPieces; ++index) {
                const highlightIndex = (reverseCentreIndices ? this.maxPieces - value : value - 1);
                const image = (
                    highlightOne && index === highlightIndex
                        ? highlightPieceImg : pieceImg
                );
                const pieceIndex = (reverseCentreIndices ? this.maxPieces - index - 1 : index);
                const centre = centres[pieceIndex];
                ctx.globalAlpha = this.getFade(pieceIndex).getEaseInOut();
                drawPiece(ctx, image, centre, pieceWidth);
            }
        } finally {
            ctx.restore();
        }
    }
}


interface Renderers {
    piecesRenderer: PlayerStateRenderer;
    scoreRenderer: PlayerStateRenderer;
    dependencies: DependencyList;
}


function usePlayerStateRenderers(
    playerType: PlayerType,
    positionLeft: boolean,
    sharedRendererState: SharedRendererState,
): Renderers {
    const gameTheme = useGameTheme();
    const playerStateController = usePlayerStateController();

    const gameSource = useGameSource();
    const game = useRune(gameSource.game);
    const maxPieces = game.getRules().getSettings().getStartingPieceCount();

    const renderersRef = useRef<Renderers | null>(null);
    let current = renderersRef.current;
    const newDependencies = [playerType, positionLeft, maxPieces, gameTheme, playerStateController];

    if (current === null || !areDependenciesEqual(newDependencies, current.dependencies)) {
        current = {
            piecesRenderer: new PlayerStateRenderer(
                playerType, !positionLeft, true,
                PlayerStateValue.PIECES, maxPieces,
                gameTheme,
                playerStateController,
                sharedRendererState,
            ),
            scoreRenderer: new PlayerStateRenderer(
                playerType, !positionLeft, false,
                PlayerStateValue.SCORE, maxPieces,
                gameTheme,
                playerStateController,
                sharedRendererState,
            ),
            dependencies: newDependencies,
        };
        renderersRef.current = current;
    }
    return current;
}


interface PlayerStateProps {
    children?: ReactNode;
    playerType: PlayerType;
    gameLayout: GameLayoutType;
    positionLeft: boolean;
    hideReactionUI: boolean;
    sharedRendererState: SharedRendererState;
}

export function PlayerStateUI({
    children, playerType, gameLayout, positionLeft, hideReactionUI, sharedRendererState,
}: PlayerStateProps) {
    const gameController = useGameController();
    const lobbyStatus = useRune(gameController.getLobbyStatus());
    const players = useRune(gameController.getPlayers());
    const directive = usePlayerStateDirective();
    const moveIndex = useRune(sharedRendererState.currentMoveIndex);
    const player = players.getPlayerByType(playerType);
    const playerConnected = useIsPlayerConnected(lobbyStatus, player);

    useEffect(() => {
        return gameController.registerPlayerStateDirectiveSink();
    }, [gameController]);

    // Use the user preferences for their player. This is needed to
    // support updating preferences without needing a new directive.
    const yourPlayer = players.getYourPlayer();
    const user = useOptionalUser();
    const { preferences: userPreferences } = useGamePreferences(user);
    let preferences = player.getPreferences();
    if (yourPlayer === playerType) {
        preferences = userPreferences;
    }
    const disabledReactions = players.areReactionsDisabled(userPreferences);

    const piecesCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const scoreCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const { piecesRenderer, scoreRenderer } = usePlayerStateRenderers(
        playerType, positionLeft, sharedRendererState,
    );

    const isActive = (directive.getActivePlayer(moveIndex) === playerType);
    const isLandscape = (gameLayout === "landscape");
    const isPortrait = (gameLayout === "portrait");
    const isSquare = (gameLayout === "square");

    let nameClassName, reactionClassName;
    if (isLandscape) {
        nameClassName = (positionLeft ? "right-0 pr-[0.2em]" : "left-0 pl-[0.2em]");
        reactionClassName = "absolute left-full -top-[0.2em] text-base";
    } else if (isPortrait) {
        nameClassName = (positionLeft ? "left-[1em]" : "right-[1em]");
        reactionClassName = "fixed left-0 top-1/2 -translate-y-1/2 text-2xl";
    } else {
        nameClassName = (positionLeft ? "left-[85%] -translate-x-1/2" : "right-[85%] translate-x-1/2");
        reactionClassName = "fixed left-0 top-[30%] -translate-y-1/2 text-2xl";
    }

    const minorTextClassName = cn(
        "grow-0",
        isLandscape && (positionLeft ? "pr-[0.6em]" : "pl-[0.5em]"),
    );
    const minorTextPClassName = "game-text p-0 m-0 text-xl text-body";
    const canvasClassName = cn(
        "inline-block transform-gpu",
        isPortrait ? "w-[4.5em] h-[13.5em]" : "w-[13.5em] h-[4.5em]",
        isSquare && "max-md:w-[3em] max-md:h-[7em]",
    );

    return (
        <div className={cn(
            "relative flex flex-col h-full pt-[1.7em] pb-[2em]",
            positionLeft ? "pl-[1em] text-right" : "pr-[1em] text-left",
            // Stop layout changes with dice moving from left to right.
            isLandscape && "mt-[4em] max-w-[min(18em,30vw)] min-w-[min(18em,30vw)]",
        )}>

            <div>
                <PlayerDescPopupButton
                    player={player}
                    yourPlayer={yourPlayer}
                    positionLeft={positionLeft}
                    playerConnected={playerConnected}
                    preferences={preferences ?? defaultGamePreferences}
                    hideReactions={userPreferences.disabledReactions}
                    popupButtonClassName={cn(
                        "absolute flex flex-row items-center -top-[0.2em] z-11 text-2xl",
                        nameClassName,
                    )}
                    buttonPlayerNameClassName={cn(
                        "tracking-tight",
                        playerConnected ? "text-body" : "text-disconnected",
                    )}
                    activePlayerNameClassName={cn(
                        "absolute underline transition-opacity duration-200 ease-[ease-in-out]",
                        positionLeft ? "left-0" : "right-0",
                        isActive ? "opacity-100" : "opacity-0",
                        playerConnected ? "text-strong" : "text-disconnected",
                    )} />

                {!hideReactionUI && yourPlayer === playerType && (
                    <ReactionUI
                        className={reactionClassName}
                        yourPlayer={yourPlayer}
                        disabledReactions={disabledReactions}
                        youDisabledReactions={userPreferences.disabledReactions} />
                )}
            </div>

            <div className="flex flex-col">
                <div className={minorTextClassName}>
                    <p className={minorTextPClassName}>
                        Pieces
                    </p>
                </div>
                <div>
                    <RenderedCanvas
                        className={canvasClassName}
                        canvasRef={piecesCanvasRef}
                        renderer={piecesRenderer}
                        updateEveryFrame={true} />
                </div>
            </div>

            <div className="flex flex-col grow items-center justify-center">
                {children && gameLayout !== "portrait" && (
                    <div className={isSquare ? undefined : "min-h-[10em]"}>
                        {children}
                    </div>
                )}
            </div>

            <div className="flex flex-col">
                <div>
                    <RenderedCanvas
                        className={canvasClassName}
                        canvasRef={scoreCanvasRef}
                        renderer={scoreRenderer}
                        updateEveryFrame={true} />
                </div>
                <div className={minorTextClassName}>
                    <p className={minorTextPClassName}>
                        Score
                    </p>
                </div>
            </div>
        </div>
    );
}
